import { memo } from "react";
import { generatePath, Link, useLocation } from "react-router";

import { cn } from "@/lib/chillnn/cn";

import { PATHS } from "@/routes/paths";

import type { FeatureTabItemType } from "@/components/features/_layouts";

const getTabArray = (hotelID: string) =>
	[
		{
			tabTitle: "一覧",
			path: generatePath(PATHS.HOTEL.ITEMS.OPTION.TOP, { hotelID }),
		},
	] satisfies FeatureTabItemType[];

type HeaderProps = {
	title: string;
	rightChildren?: React.ReactNode;
};

type Props = {
	hotelID: string;
	headerProps: HeaderProps;
};

const OptionHeaderAndTabsInner = memo(({ title, rightChildren }: HeaderProps) => {
	return (
		<div className="flex flex-wrap items-center justify-between gap-y-2 tablet:min-h-11">
			<div className="flex items-center gap-12">
				<h2 className="font-semibold tracking-[0.15rem] laptop:text-lg">{title}</h2>
			</div>
			{rightChildren}
		</div>
	);
});

const OptionTab = memo(({ tabArray, hotelID }: { tabArray: FeatureTabItemType[]; hotelID: string }) => {
	const { pathname } = useLocation();
	const formattedPath = pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
	const activeTab = tabArray.find((tab) => tab.path === formattedPath)?.tabTitle;

	const baseClassName =
		"hidden tablet:flex items-center gap-x-2 px-2 py-2 text-[15px] tracking-wider transition-colors duration-300 ease-in-out laptop:text-base";

	// 「日ごとの在庫管理」タブがアクティブかどうか
	const isStockDeprecatedTabActive =
		formattedPath === generatePath(PATHS.HOTEL.ITEMS.OPTION.STOCK_DEPRECATED, { hotelID });

	return (
		<div className="inline-flex gap-x-5">
			{tabArray.map((tab) => (
				<Link
					key={tab.tabTitle}
					className={cn(
						baseClassName,
						activeTab === tab.tabTitle
							? "rounded-t-lg border-b-2 border-b-brand bg-white font-medium"
							: "border-b-2 border-b-white text-zinc-400 hover:border-b-zinc-200 hover:text-black",
					)}
					to={tab.path}
					viewTransition
				>
					<h4>{tab.tabTitle}</h4>
				</Link>
			))}
			{/* 日ごとの在庫管理 - テキスト直置き（リンクとして deprecated ページへ遷移） */}
			<Link
				to={generatePath(PATHS.HOTEL.ITEMS.OPTION.STOCK_DEPRECATED, { hotelID })}
				className={cn(
					baseClassName,
					isStockDeprecatedTabActive
						? "rounded-t-lg border-b-2 border-b-brand bg-white font-medium"
						: "border-b-2 border-b-white text-zinc-400 hover:border-b-zinc-200 hover:text-black",
				)}
				viewTransition
			>
				<h4>日ごとの在庫管理</h4>
			</Link>
		</div>
	);
});

export const OptionHeaderAndTabs = ({ hotelID, headerProps }: Props) => {
	const tabArray = getTabArray(hotelID);

	return (
		<header className={cn("sticky z-20 border-b border-b-zinc-100 bg-white px-4 py-4 pb-0 laptop:px-12")}>
			<div className="flex flex-col gap-y-2">
				<OptionHeaderAndTabsInner {...headerProps} />
				<OptionTab tabArray={tabArray} hotelID={hotelID} />
			</div>
		</header>
	);
};
